/**
 * LandingPrimaryCta — directe pre-sale CTA voor de Powersure landing.
 *
 * P0 Flow-fix (Feb 2026):
 * De landing CTA mag NIET meer naar `/energy-control-intake` (vragen) gaan.
 * Klik gaat direct naar Stripe Checkout (subscription mode) op
 * `/api/subscriptions/monitoring/checkout`. Setup-vragen gebeuren pas
 * NA aankoop in `/app`-onboarding.
 *
 * Gedrag:
 *  - klik → POST naar /api/subscriptions/monitoring/checkout
 *  - bij 200 → window.location.href = data.url (Stripe)
 *  - bij 503 → "Stripe wordt nog ingesteld" hint (preview-env)
 *  - bij overige fout → algemene fout-microcopy
 *  - geen vragen, geen tussen-formulier, geen quickscan.
 */
import { useState } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { trackFunnelEvent, getAttribution } from '@/utils/tracking';

export default function LandingPrimaryCta({
  ctaId = 'landing_primary',
  label = 'Start Mijn Powersure',
  variant = 'primary',
  className = '',
  testId = 'landing-primary-cta',
  fullWidth = true,
}) {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const onClick = async () => {
    if (loading) return;
    setErrorMsg('');
    setLoading(true);
    try {
      try {
        trackFunnelEvent('homepage_primary_cta_clicked', {
          meta: { cta_id: ctaId, label, target: 'stripe_checkout_direct' },
        });
        trackFunnelEvent('payment_clicked', { plan: 'monitoring_monthly_intro' });
      } catch { /* ignore */ }
      if (typeof window !== 'undefined' && window.pwTrack) {
        try { window.pwTrack('cta_to_checkout', { cta_id: ctaId }); } catch { /* ignore */ }
      }

      const attr = getAttribution() || {};
      const sessionId =
        (typeof window !== 'undefined' && sessionStorage.getItem('powersure_session')) || null;
      const res = await fetch('/api/subscriptions/monitoring/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: null,
          lead_id: null,
          intake_id: null,
          ref_code: null,
          session_id: sessionId,
          gclid: attr.gclid || null,
          gbraid: attr.gbraid || null,
          wbraid: attr.wbraid || null,
          utm_source: attr.utm_source || null,
          utm_medium: attr.utm_medium || null,
          utm_campaign: attr.utm_campaign || null,
          utm_term: attr.utm_term || null,
          utm_content: attr.utm_content || null,
          landing_url: attr.landing_url || null,
          referrer: attr.referrer || null,
        }),
      });
      if (res.status === 503) {
        setErrorMsg('Abonneren is in deze omgeving nog niet actief. Probeer het via de productie-app.');
        return;
      }
      if (!res.ok) {
        setErrorMsg('Checkout kon niet worden gestart. Probeer het later opnieuw.');
        return;
      }
      const data = await res.json();
      if (!data?.url) {
        setErrorMsg('Checkout-URL ontbreekt. Probeer het later opnieuw.');
        return;
      }
      try {
        trackFunnelEvent('checkout_started', { plan: 'monitoring_monthly_intro', amount: data.amount });
      } catch { /* ignore */ }
      window.location.href = data.url;
    } catch (_err) {
      setErrorMsg('Netwerkfout. Probeer het later opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  const baseCls = variant === 'primary'
    ? 'inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold text-[15px] transition-colors shadow-lg shadow-emerald-900/40 disabled:opacity-60'
    : 'inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-md border border-white/15 bg-white/5 text-white/90 text-sm font-medium hover:bg-white/10 transition-colors disabled:opacity-60';

  return (
    <div className={fullWidth ? 'w-full' : ''} data-testid={`${testId}-wrap`}>
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        className={[fullWidth ? 'w-full' : '', baseCls, className].filter(Boolean).join(' ')}
        data-testid={testId}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {loading ? 'Bezig…' : label}
        {!loading ? <ArrowRight className="w-4 h-4" /> : null}
      </button>
      {errorMsg && (
        <p
          className="text-[12px] text-rose-200 bg-rose-500/10 border border-rose-400/30 rounded-md px-2.5 py-1.5 mt-2"
          data-testid={`${testId}-error`}
        >
          {errorMsg}
        </p>
      )}
      {!errorMsg && (
        <p
          className="text-[11px] text-white/55 mt-2 leading-snug"
          data-testid={`${testId}-microcopy`}
        >
          Betaal met <strong className="text-white/75">iDEAL</strong>, kaart of SEPA-incasso. Maandelijks opzegbaar.
        </p>
      )}
    </div>
  );
}

/**
 * LandingSecondaryCta — "Bekijk hoe het werkt" scroll-CTA (geen vragen).
 * Scrollt naar de app-preview sectie of een ander id op de pagina.
 */
export function LandingSecondaryCta({
  targetTestId = 'powersure-app-preview-section',
  label = 'Bekijk hoe het werkt',
  className = '',
  testId = 'landing-secondary-cta',
  fullWidth = true,
}) {
  const onClick = () => {
    try {
      trackFunnelEvent('homepage_secondary_cta_clicked', {
        meta: { cta_id: 'landing_how_it_works', target: targetTestId },
      });
    } catch { /* ignore */ }
    const el = document.querySelector(`[data-testid="${targetTestId}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        fullWidth ? 'flex-1 w-full' : '',
        'inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-md border border-white/15 bg-white/5 text-white/90 text-sm font-medium hover:bg-white/10 transition-colors',
        className,
      ].filter(Boolean).join(' ')}
      data-testid={testId}
    >
      {label}
    </button>
  );
}
